import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { VxFormFieldErrorDirective } from '@ui/form-field/form-field-error.directive';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { VxKvEditorComponent } from '@ui/kv-editor/kv-editor.component';
import { VxRadioGroupItemComponent } from '@ui/radio-group/radio-group-item.component';
import { VxRadioGroupComponent } from '@ui/radio-group/radio-group.component';
import { AlertCorrelatorType } from 'src/app/models/alert';
import { AlertProcessorConfigurationForm } from './configuration-form';

/**
 * Correlation settings of an alert processor: the method, and for the
 * statistical one its threshold and label weights. The parent owns the
 * form group (see `configuration-form.ts`); this component only renders it.
 */
@Component({
  selector: 'app-alert-configuration-wizard',
  templateUrl: './configuration-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    VxFormFieldComponent,
    VxFormFieldErrorDirective,
    VxInputComponent,
    VxKvEditorComponent,
    VxRadioGroupComponent,
    VxRadioGroupItemComponent,
  ],
  host: { class: 'block' },
})
export class AlertConfigurationWizardComponent {
  readonly configurationFG = input.required<AlertProcessorConfigurationForm>();

  private readonly correlatorType = signal<AlertCorrelatorType>(
    AlertCorrelatorType.STATISTICAL
  );

  protected readonly isStatistical = computed(
    () => this.correlatorType() === AlertCorrelatorType.STATISTICAL
  );

  /** The correlator values, for the template's radio items. */
  protected readonly correlator = AlertCorrelatorType;

  constructor() {
    this.initCorrelatorTracking();
  }

  /** Mirrors the correlator control into a signal so the statistical section can toggle. */
  private initCorrelatorTracking(): void {
    effect(onCleanup => {
      const control = this.configurationFG().controls.correlatorType;
      this.correlatorType.set(control.value);
      const subscription = control.valueChanges.subscribe(value =>
        this.correlatorType.set(value)
      );
      onCleanup(() => subscription.unsubscribe());
    });
  }
}
